import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Activity } from "../../../../types"
import { useToast } from "../../../hooks"
import { useLazyGetThreadQuery } from "../../../../redux/queries"

export const getActivityRoute = (activity: Activity): string | null => {
  const { category, item_id, type } = activity

  if (!item_id) return null

  // SMS follow-up requires special handling with API call
  if (type === "sms_followup_sent") {
    return "async" // Special marker for async navigation
  }

  switch (category) {
    case "subscription":
      return `/engagements/${item_id}`
    case "lead":
      return `/leads/${item_id}`
    default:
      return null
  }
}

export const useActivityNavigation = (
  activity: Activity,
  returnUrl?: string,
  brandName?: string
) => {
  const navigate = useNavigate()
  const { pushErrorToast } = useToast()
  const [isNavigating, setIsNavigating] = useState(false)
  const [getThread] = useLazyGetThreadQuery()

  const route = getActivityRoute(activity)
  const isClickable = !!route

  const handleClick = useCallback(async () => {
    if (!route) return

    // Handle SMS follow-up with async API call
    if (route === "async" && activity.type === "sms_followup_sent") {
      try {
        setIsNavigating(true)
        const params: { subscription_id?: string; lead_id?: string } = {}

        if (activity.category === "subscription") {
          params.subscription_id = String(activity.item_id)
        } else if (activity.category === "lead") {
          params.lead_id = String(activity.item_id)
        }

        const result = await getThread(params).unwrap()

        if (result && result._id) {
          // Navigate directly - ConversationsThread will handle loading by _id
          navigate(`/conversations/sms/${result._id}`)
        } else {
          pushErrorToast("Conversation not found")
        }
      } catch (error) {
        console.error("Error fetching thread:", error)
        pushErrorToast("Unable to load conversation. Please try again.")
      } finally {
        setIsNavigating(false)
      }
    } else {
      // Direct navigation for other routes
      // Add returnUrl parameter if provided (for brand context)
      if (returnUrl) {
        navigate(
          `${route}?returnUrl=${returnUrl}&brandName=${encodeURIComponent(
            brandName || ""
          )}`
        )
      } else {
        navigate(route)
      }
    }
  }, [
    activity,
    route,
    navigate,
    pushErrorToast,
    getThread,
    returnUrl,
    brandName
  ])

  return {
    isClickable,
    isNavigating,
    handleClick
  }
}
